import { useState } from 'react';
import type { FormEvent } from 'react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import {
  Bath,
  BedDouble,
  Building2,
  Camera,
  CheckCircle2,
  Edit,
  Home,
  Image as ImageIcon,
  MapPin,
  Plus,
  Ruler,
  ShieldCheck,
  Star,
  Trash2,
} from 'lucide-react';
import { Toaster, toast } from 'sonner';
import { ImageWithFallback } from './ui/ImageWithFallback';
import { ImageUploader } from './ImageUploader';
import { AnyComponent } from '@fullcalendar/core/preact';
import { CallAPIPostPromise } from 'Common/commaon';
import { TwoButtonAlert } from 'Common/twoButtonAlert';
import { IoTrashOutline } from 'react-icons/io5';
import { getDecodeId } from 'slices/userProfile/thunk';
import './property-manager.css';

interface PropertyManagerProps {
  properties: any[];
  currentUserId: string;
  onAddProperty: (property: Omit<any, 'property_id' | 'createdAt'>) => void;
  onUpdateProperty: (id: string, property: Partial<AnyComponent>) => void;
  onDeleteProperty: (id: string) => void;
  onSetPrimary: (id: string) => void;
  onBack?: () => void;
}

const formatPropertyCount = (value: unknown, singular: string) => {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return "";
  const formatted = Number.isInteger(amount) ? String(amount) : amount.toFixed(1).replace(/\.0$/, "");
  return `${formatted} ${amount === 1 ? singular : `${singular}s`}`;
};

const formatCountry = (value: unknown) => {
  const country = String(value || "").trim();
  return /^united state$/i.test(country) ? "United States" : country;
};

export function PropertyManager({
  properties,
  currentUserId,
  onAddProperty,
  onUpdateProperty,
  onDeleteProperty,
  onSetPrimary,
  onBack
}: PropertyManagerProps) {
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingProperty, setEditingProperty] = useState<any | null>(null);
  const [formData, setFormData] = useState({
    address_line_1: '',
    city: '',
    state: '',
    zip_code: '',
    country: 'USA',
    property_type: 'single-family' as any['property_type'],
    square_feet: '',
    bedrooms: '',
    bathrooms: '',
    images: [] as string[],
    is_primary: false
  });
  const [alertDetails,setAlertDetails]= useState({
    status: false,
    title: "Warning!",
    message: "You’re about to delete your account forever,",
    icon: IoTrashOutline,
    button1Text: "Keep it",
    button2Text: "Delete",
    button1Style: "px-4 py-2 rounded-lg bg-gray-100 text-black hover:bg-gray-200 transition",
    button2Style: "px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 transition",
    onButton1Click: () => console.log("Saved "),
    onButton2Click: () => console.log("Deleted "),
  })
  const resetForm = () => {
    setFormData({
      address_line_1: '',
      city: '',
      state: '',
      zip_code: '',
      country: 'USA',
      property_type: 'single-family',
      square_feet: '',
      bedrooms: '',
      bathrooms: '',
      images: [],
      is_primary: false
    });
  };

  const closePropertyDialog = () => {
    setShowAddDialog(false);
    setEditingProperty(null);
    resetForm();
  };

  const handleAddProperty = () => {
    setEditingProperty(null);
    resetForm();
    setShowAddDialog(true);
  };

  const handleEditProperty = (property: any) => {
    setEditingProperty(property);
    setFormData({
      address_line_1: property.address_line_1,
      city: property.city,
      state: property.state,
      zip_code: property.zip_code,
      country: property.country,
      property_type: property.property_type,
      square_feet: property.square_feet?.toString() || '',
      bedrooms: property.bedrooms?.toString() || '',
      bathrooms: property.bathrooms?.toString() || '',
      images: property.images || [],
      is_primary: property.is_primary || false
    });
    
    setShowAddDialog(true);
  };

  const handleSubmit = (event?: FormEvent<HTMLFormElement>) => {
    event?.preventDefault();
    
    if (!formData.address_line_1 || !formData.city || !formData.state || !formData.zip_code || !formData.country) {
      toast.error('Please fill in all required fields');
      return;
    }

    const propertyData = {
      homeowner_id: currentUserId,
      address_line_1: formData.address_line_1,
      city: formData.city,
      state: formData.state,
      zip_code: formData.zip_code,
      country: formData.country,
      property_type: formData.property_type,
      square_feet: formData.square_feet ? parseInt(formData.square_feet) : undefined,
      bedrooms: formData.bedrooms ? parseInt(formData.bedrooms) : undefined,
      bathrooms: formData.bathrooms ? parseFloat(formData.bathrooms) : undefined,
      images: formData.images.length > 0 ? formData.images : undefined,
      is_primary: formData.is_primary
    };

    if (editingProperty) {
      CallAPIPostPromise(`/profile/properties/update/${editingProperty.property_id}`,propertyData).then((res)=>{
        onUpdateProperty(editingProperty.property_id, propertyData);
        toast.success('Property updated successfully!');

      }).catch((e)=>{
        console.log(e)
      })
    } else {
      
      CallAPIPostPromise("/profile/properties/add",propertyData).then((res)=>{
        onAddProperty(propertyData);
      toast.success('Property added successfully!');
      }).catch((e)=>{
        console.log(e)
      })
      
    }

    closePropertyDialog();
  };

  const handleDeleteProperty = (id: string, address: string) => {
    setAlertDetails({
      status: true,
      title: "Warning!",
      message: `You’re about to Delete this property at ${address}`,
      icon: IoTrashOutline,
      button1Text: "Close",
      button2Text: "Delete Property",
      button1Style: "px-4 py-2 rounded-lg bg-gray-100 text-black hover:bg-gray-200 transition",
      button2Style: "px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 transition",
      onButton1Click: () => console.log("Saved "),
      onButton2Click: () => {
        // setLoading(true)

        CallAPIPostPromise(`/profile/properties/delete/${id}`,{}).then((res)=>{
        
      onDeleteProperty(id);
      toast.success('Property deleted successfully');
          
        }).catch((e)=>{
          console.log(e)
        }).finally(()=>{
        })
      },
    })
  
  };

  const handleSetPrimary = (id: string) => {
    CallAPIPostPromise(`/profile/properties/set-primary/${id}`,{
      homeowner_id:getDecodeId()
    }).then((res)=>{
        
      onSetPrimary(id);
 
      toast.success('Primary property updated');
       
     }).catch((e)=>{
       console.log(e)
     }).finally(()=>{
     })

  };

  const primaryProperty = properties.find(
    (property) => property.is_primary === true || Number(property.is_primary) === 1
  );
  const photoCount = properties.reduce(
    (total, property) => total + (Array.isArray(property.images) ? property.images.length : 0),
    0
  );

  return (
    <div className="property-manager-page">
      <main>
        <section className="property-manager-hero">
          <div className="property-manager-hero__copy">
            <span className="property-manager-hero__eyebrow">
              <Building2 aria-hidden="true" /> Home portfolio
            </span>
            <h1>Manage your properties</h1>
            <p>Keep each property’s address, details, and photos organized for faster project setup.</p>
          </div>
          <Button onClick={handleAddProperty} size="lg" className="property-manager-add-button">
            <Plus aria-hidden="true" />
            Add property
          </Button>
          <div className="property-manager-hero__stats" aria-label="Property summary">
            <div><strong>{properties.length}</strong><span>Total properties</span></div>
            <div><strong>{primaryProperty ? 'Set' : 'Not set'}</strong><span>Primary property</span></div>
            <div><strong>{photoCount}</strong><span>Uploaded photos</span></div>
          </div>
        </section>

        {properties.length === 0 ? (
          <section className="property-manager-empty">
            <span className="property-manager-empty__icon"><Home aria-hidden="true" /></span>
            <span className="property-manager-empty__eyebrow">Your property hub</span>
            <h2>Add your first property</h2>
            <p>Create a reusable property profile so addresses and home details are ready for future projects.</p>
            <Button onClick={handleAddProperty} size="lg">
              <Plus aria-hidden="true" /> Add your first property
            </Button>
          </section>
        ) : (
          <div className="property-manager-layout">
            <section className="property-manager-collection" aria-labelledby="saved-properties-title">
              <div className="property-manager-section-heading">
                <div>
                  <span>Saved homes</span>
                  <h2 id="saved-properties-title">Your properties</h2>
                </div>
                <p>{properties.length} {properties.length === 1 ? 'property' : 'properties'}</p>
              </div>

              <div className="property-manager-grid">
                {properties.map((property) => {
                  const isPrimary = property.is_primary === true || Number(property.is_primary) === 1;
                  const propertyImages = Array.isArray(property.images) ? property.images : [];
                  return (
                    <Card key={property.property_id} className="property-manager-card">
                      {propertyImages.length > 0 ? (
                        <div className="property-manager-card__media">
                          <ImageWithFallback
                            src={propertyImages[0]}
                            alt={property.address_line_1 || 'Property'}
                            className="w-full h-full object-cover"
                          />
                          <span className="property-manager-card__photo-count">
                            <Camera aria-hidden="true" /> {propertyImages.length}
                          </span>
                        </div>
                      ) : (
                        <div className="property-manager-card__media property-manager-card__media--empty">
                          <span><ImageIcon aria-hidden="true" /></span>
                          <small>Add a cover photo</small>
                        </div>
                      )}

                      <div className="property-manager-card__body">
                        <div className="property-manager-card__heading">
                          <div className="property-manager-card__title-wrap">
                            <div className="property-manager-card__title-row">
                              <h3 title={property.address_line_1}>{property.address_line_1 || 'Untitled property'}</h3>
                              {isPrimary ? (
                                <span className="property-manager-card__primary"><Star aria-hidden="true" /> Primary</span>
                              ) : null}
                            </div>
                            <div className="property-manager-card__location">
                              <MapPin aria-hidden="true" />
                              <p>{property.city}, {property.state} {property.zip_code}</p>
                            </div>
                            {formatCountry(property.country) ? (
                              <small className="property-manager-card__country">{formatCountry(property.country)}</small>
                            ) : null}
                          </div>
                        </div>

                        <div className="property-manager-card__facts">
                          {property.property_type ? (
                            <span><Home aria-hidden="true" /> {property.property_type.replace('-', ' ')}</span>
                          ) : null}
                          {property.bedrooms ? (
                            <span><BedDouble aria-hidden="true" /> {formatPropertyCount(property.bedrooms, 'bed')}</span>
                          ) : null}
                          {property.bathrooms ? (
                            <span><Bath aria-hidden="true" /> {formatPropertyCount(property.bathrooms, 'bath')}</span>
                          ) : null}
                          {property.square_feet ? (
                            <span><Ruler aria-hidden="true" /> {Number(property.square_feet).toLocaleString()} sq ft</span>
                          ) : null}
                        </div>

                        <div className="property-manager-card__actions">
                          <Button
                            variant="outline"
                            size="sm"
                            className="property-manager-card__action"
                            onClick={() => handleEditProperty(property)}
                          >
                            <Edit aria-hidden="true" /> Edit details
                          </Button>
                          {!isPrimary ? (
                            <Button
                              variant="outline"
                              size="sm"
                              className="property-manager-card__action"
                              onClick={() => handleSetPrimary(property.property_id)}
                            >
                              <Star aria-hidden="true" /> Set primary
                            </Button>
                          ) : null}
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDeleteProperty(property.property_id, property.address_line_1)}
                            className="property-manager-card__action property-manager-card__action--danger"
                            aria-label={`Delete ${property.address_line_1 || 'property'}`}
                          >
                            <Trash2 aria-hidden="true" />
                          </Button>
                        </div>
                      </div>
                    </Card>
                  );
                })}
              </div>
            </section>

            <aside className="property-manager-guide">
              <span className="property-manager-guide__icon"><ShieldCheck aria-hidden="true" /></span>
              <span className="property-manager-guide__eyebrow">Property setup</span>
              <h2>Keep project details ready</h2>
              <p>A complete property profile saves time when you start a project or connect with a contractor.</p>
              <ul>
                <li><CheckCircle2 aria-hidden="true" /><span><strong>Confirm the address</strong><small>Used for projects and local recommendations.</small></span></li>
                <li><CheckCircle2 aria-hidden="true" /><span><strong>Add home details</strong><small>Bedrooms, bathrooms, type, and square footage.</small></span></li>
                <li><CheckCircle2 aria-hidden="true" /><span><strong>Upload clear photos</strong><small>The first image becomes the cover photo.</small></span></li>
              </ul>
            </aside>
          </div>
        )}
      </main>

      <TwoButtonAlert setDetails={setAlertDetails} details={alertDetails} />

      <Dialog
        open={showAddDialog}
        onOpenChange={(open) => {
          if (open) setShowAddDialog(true);
          else closePropertyDialog();
        }}
      >
        <DialogContent className="tailwind-prefix property-manager-dialog">
          <DialogHeader className="property-manager-dialog__header">
            <span className="property-manager-dialog__header-icon"><Home aria-hidden="true" /></span>
            <div>
              <span className="property-manager-dialog__eyebrow">Property profile</span>
              <DialogTitle>{editingProperty ? 'Edit property' : 'Add a new property'}</DialogTitle>
              <p>{editingProperty ? 'Keep your property information accurate and up to date.' : 'Add the home details you will reuse across RELiBUILD.'}</p>
            </div>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="property-manager-form">
            <div className="property-manager-dialog__body">
              <section className="property-manager-form__section">
                <div className="property-manager-form__section-title">
                  <span><MapPin aria-hidden="true" /></span>
                  <div><h3>Property address</h3><p>Where is this property located?</p></div>
                </div>
                <div className="property-manager-form__grid">
                  <div className="property-manager-field property-manager-field--wide">
                    <Label htmlFor="address_line_1">Street address <b>*</b></Label>
                    <Input id="address_line_1" placeholder="123 Main St, Apt 4B" value={formData.address_line_1} onChange={(e) => setFormData({ ...formData, address_line_1: e.target.value })} required />
                  </div>
                  <div className="property-manager-field">
                    <Label htmlFor="city">City <b>*</b></Label>
                    <Input id="city" placeholder="San Francisco" value={formData.city} onChange={(e) => setFormData({ ...formData, city: e.target.value })} required />
                  </div>
                  <div className="property-manager-field">
                    <Label htmlFor="state">State <b>*</b></Label>
                    <Input id="state" placeholder="CA" value={formData.state} onChange={(e) => setFormData({ ...formData, state: e.target.value })} required />
                  </div>
                  <div className="property-manager-field">
                    <Label htmlFor="zip_code">ZIP code <b>*</b></Label>
                    <Input id="zip_code" inputMode="numeric" placeholder="94102" maxLength={10} value={formData.zip_code} onChange={(e) => setFormData({ ...formData, zip_code: e.target.value })} required />
                  </div>
                  <div className="property-manager-field">
                    <Label htmlFor="country">Country <b>*</b></Label>
                    <Input id="country" placeholder="United States" value={formData.country} onChange={(e) => setFormData({ ...formData, country: e.target.value })} required />
                  </div>
                </div>
              </section>

              <section className="property-manager-form__section">
                <div className="property-manager-form__section-title">
                  <span><Home aria-hidden="true" /></span>
                  <div><h3>Property details</h3><p>Help us understand the home.</p></div>
                </div>
                <div className="property-manager-form__grid property-manager-form__grid--details">
                  <div className="property-manager-field property-manager-field--wide">
                    <Label htmlFor="property_type">Property type <b>*</b></Label>
                    <Select value={formData.property_type} onValueChange={(value) => setFormData({ ...formData, property_type: value as any['property_type'] })}>
                      <SelectTrigger id="property_type"><SelectValue /></SelectTrigger>
                      <SelectContent className="property-manager-select-menu">
                        <SelectItem value="single-family">Single Family</SelectItem>
                        <SelectItem value="multi-family">Multi Family</SelectItem>
                        <SelectItem value="condo">Condo</SelectItem>
                        <SelectItem value="townhouse">Townhouse</SelectItem>
                        <SelectItem value="commercial">Commercial</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="property-manager-field">
                    <Label htmlFor="square_feet">Square feet</Label>
                    <Input id="square_feet" type="number" min="0" placeholder="2,000" value={formData.square_feet} onChange={(e) => setFormData({ ...formData, square_feet: e.target.value })} />
                  </div>
                  <div className="property-manager-field">
                    <Label htmlFor="bedrooms">Bedrooms</Label>
                    <Input id="bedrooms" type="number" min="0" placeholder="3" value={formData.bedrooms} onChange={(e) => setFormData({ ...formData, bedrooms: e.target.value })} />
                  </div>
                  <div className="property-manager-field">
                    <Label htmlFor="bathrooms">Bathrooms</Label>
                    <Input id="bathrooms" type="number" min="0" step="0.5" placeholder="2.5" value={formData.bathrooms} onChange={(e) => setFormData({ ...formData, bathrooms: e.target.value })} />
                  </div>
                </div>
                <label className="property-manager-primary-toggle">
                  <input type="checkbox" checked={formData.is_primary} onChange={(e) => setFormData({ ...formData, is_primary: e.target.checked })} />
                  <span><Star aria-hidden="true" /></span>
                  <span><strong>Set as primary property</strong><small>Use this property as your default across RELiBUILD.</small></span>
                </label>
              </section>

              <section className="property-manager-form__section">
                <div className="property-manager-form__section-title">
                  <span><Camera aria-hidden="true" /></span>
                  <div><h3>Property photos</h3><p>Add up to 10 images. The first image is your cover.</p></div>
                </div>
                <div className="property-manager-upload">
                  <ImageUploader
                    urls={formData.images}
                    path="properties"
                    onImagesChange={(images) => setFormData((current) => ({ ...current, images }))}
                    maxImages={10}
                  />
                </div>
              </section>
            </div>

            <div className="property-manager-dialog__footer">
              <span><ShieldCheck aria-hidden="true" /> Your property details stay private.</span>
              <div>
                <Button type="button" variant="outline" className="property-manager-dialog__cancel" onClick={closePropertyDialog}>Cancel</Button>
                <Button type="submit" className="property-manager-dialog__submit">{editingProperty ? 'Save changes' : 'Add property'}</Button>
              </div>
            </div>
          </form>
        </DialogContent>
      </Dialog>
      <Toaster />
    </div>
  );
}
